import {Injectable} from "@angular/core";
import {Observable} from "rxjs/Observable";
import gql from "graphql-tag";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {Supplier} from "../../../shared/view-model/crm/supplier.type";
import {addressFragments} from "../../../core/visbos-core/services/address.service";
import {NgRedux} from "@angular-redux/store";
import {ApolloQueryResult} from "apollo-client";
import {MessageType} from "../../../shared/redux-store/session/session.types";
import {businessRelationFragments} from "../../../organization/services/administration_unit_service/administration-unit.service";
import {isApolloError} from "apollo-client/errors/ApolloError";
import {IAppState} from "../../../shared/redux-store";
import {SessionActions} from "../../../shared/actions";
import {catchError, map} from "rxjs/operators";
import {empty} from "rxjs/observable/empty";

export const supplierFragments = {
  supplier: gql`
    fragment SupplierFields on Supplier {
      cuid, label, is_editable,
      name, address_name,
      address {...AddressFields}
      administration_unit {cuid, label}
    }
    ${addressFragments['address']}
  `
};

export const fetchSuppliersQuery = gql`
  query FetchSuppliers($AU: String) {
    fetchSuppliers(administration_unit_cuid: $AU){
      ...SupplierFields
      business_relations(adminUnitCuid: $AU) {
        ...BusinessRelationFields
      }
    }
  }
  ${businessRelationFragments.businessRelation}
  ${supplierFragments['supplier']}
`;

export const createOrUpdateSupplierBusinessRelationMutation = gql`
  mutation CreateOrUpdateSupplierBusinessRelation($businessRelCuid: String, $adminUnitCuid: String, $supplierCuid: String, $businessRelationDataInput: SupplierBusinessRelationInput) {
    createOrUpdateSupplierBusinessRelations(businessRelCuid: $businessRelCuid, adminUnitCuid: $adminUnitCuid, supplierCuid: $supplierCuid, businessRelationDataInput: $businessRelationDataInput) {
      ...SupplierFields
      business_relations(adminUnitCuid: $adminUnitCuid) {
        ...BusinessRelationFields
      }
    }
  }
  ${businessRelationFragments.businessRelation}
  ${supplierFragments['supplier']}
`;

export interface CreateOrUpdateSupplierBusinessRelationMutationResult {
  createOrUpdateSupplierBusinessRelationMutation: Supplier;
}

@Injectable()
export class SupplierService extends VisbosService {

  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getSupplierList(administration_unit_cuid?: string | Observable<string>): ApolloQueryObservable<Supplier[]> {


    let data$ = this.fetchItems({
      query: fetchSuppliersQuery,
      variables: {
        AU: administration_unit_cuid ? administration_unit_cuid : this.activeAdminUnitCuid$
      },
    });

    let model$ = data$.pipe(map(({data}: any) => data.fetchSuppliers));

    return <ApolloQueryObservable<Supplier[]>>model$;
  }


  public getSupplier(cuid: string): ApolloQueryObservable<Supplier> {

    let data$ = this.fetchItems({
      query: gql`
        query FetchSupplier($cuid: String) {
          fetchSuppliers(cuid: $cuid){
            ...SupplierFields
          }
        }

        ${supplierFragments['supplier']}
      `,
      variables: {
        cuid: cuid
      },
    });

    let model$ = data$.pipe(map(({data}: any) => data.fetchSuppliers[0]));

    return <ApolloQueryObservable<Supplier>>model$;
  }

  public createSupplier(supplierInput, relationsDataInput?: any[]): Observable<ApolloQueryResult<{} | Supplier>> {

    let q$ = this.mutate<Supplier>({
      mutation: gql`
        mutation CreateSupplier($supplier: SupplierInput!, $relDataInput: [SupplierBusinessRelationInput], $AU: String) {
          addSupplier(supplier: $supplier, businessRelationsDataInput: $relDataInput) {
            ...SupplierFields
            business_relations(adminUnitCuid: $AU) {
              ...BusinessRelationFields
            }
          }
        }
        ${businessRelationFragments.businessRelation}
        ${supplierFragments['supplier']}
      `,
      variables: {
        supplier: supplierInput,
        relDataInput: relationsDataInput
      },
      refetchQueries: ['FetchSuppliers']
    });

    return q$;
  }


  public deleteSupplier(cuid: string): Observable<{} | ApolloQueryResult<boolean>> {
    return this.mutate<boolean>({
      mutation: gql`
        mutation DeleteSupplier($cuid: String!) {
          deleteSupplier(cuid: $cuid)
        }
      `,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['FetchSuppliers']

    }).pipe(
      catchError(err => {
        console.info("ERR", [err]);
        if (isApolloError(err) && err.graphQLErrors && err.graphQLErrors.length > 0) {
          if (err.graphQLErrors.find(e => e.message.includes('SQLSTATE[23000]'))) {
            this.sessionActions.pushMessage('Der Lieferant kann nicht gelöscht werden, da Artikel existieren, die diesem Lieferanten zugeordnet sind!', MessageType.WARNING);
            return empty();
          }
        }
        throw err;
      })
    );
  }

  public updateSupplier(cuid: string, data, relationsDataInput?: any[]): Observable<ApolloQueryResult<Supplier>> {
    return this.mutate({
      mutation: gql`
        mutation UpdateSupplier($cuid: String!, $supplier: SupplierInput!, $relDataInput: [SupplierBusinessRelationInput], $adminUnitCuid: String) {
          updateSupplier(cuid: $cuid, supplier: $supplier, businessRelationsDataInput: $relDataInput) {
            ...SupplierFields
            business_relations(adminUnitCuid: $adminUnitCuid) {
              ...BusinessRelationFields
            }
          }
        }
        ${supplierFragments['supplier']}
        ${businessRelationFragments.businessRelation}
      `,
      variables: {
        cuid: cuid,
        supplier: data,
        relDataInput: relationsDataInput,
        adminUnitCuid: this.ngRedux.getState().session.activeAdminUnit
      }
    }).pipe(map(({data}: any) => data.updateSupplier));
  }

  public setSupplierBusinessRelations(supplierCuid: string, adminUnitCuid: string, relationDataInput: any, relationCuid?: string): Observable<ApolloQueryResult<CreateOrUpdateSupplierBusinessRelationMutationResult>> {
    return this.mutate({
      mutation: createOrUpdateSupplierBusinessRelationMutation,
      variables: {
        businessRelCuid: relationCuid,
        adminUnitCuid: adminUnitCuid,
        supplierCuid: supplierCuid,
        businessRelationDataInput: relationDataInput
      }
    });
  }

}




// WEBPACK FOOTER //
// ./src/app/crm/services/supplier/supplier.service.ts